import { Input, InputNumber, Select, Spin } from 'antd';
import Form from 'antd/lib/form';
import { MandateLinkFormType } from 'api/nach';
import { handleOnlyDgit } from 'features/nach/utils/handleOnlyDigit';
import { useEffect, useState, useRef } from 'react';

type MandateLinkGridProps = {
  isLoading: boolean;
  data?: MandateLinkFormType;
  setMandateFormData?: (data: MandateLinkFormType) => void;
  setMandateAmount: (data: number) => void;
  mandateAmount: number;
  autoPay?: boolean;
  selectedEnachTabValue?: boolean;
  setAutoPay: (data: boolean) => void;
  setMaxBillAmount: (data: number) => void;
  maxBillAmount: number;
  CIFlist?: string[]; // Change here
  selectedAutopayTabValue?: boolean;
  onCIFChange: (cifId: string) => void;
  setSelectedAutopayTabValue: (value: boolean) => void;
  setSelectedEnachTabValue: (value: boolean) => void;
};

function MandateLinkForm({
  isLoading,
  data,
  setMandateAmount,
  mandateAmount,
  autoPay,
  setAutoPay,
  maxBillAmount,
  setMaxBillAmount,
  CIFlist,
  selectedAutopayTabValue,
  selectedEnachTabValue,
  onCIFChange,
  setSelectedAutopayTabValue,
  setSelectedEnachTabValue,
}: MandateLinkGridProps) {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState<MandateLinkFormType | undefined>(data);
  const [previousMandateAmount, setPreviousMandateAmount] = useState<number>(mandateAmount);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [isManuallyEdited, setIsManuallyEdited] = useState<boolean>(false);

  // Use ref to hold the previous mandate amount
  const previousMandateAmountRef = useRef<number>(mandateAmount);

  // Ensure previousMandateAmount is updated properly
  useEffect(() => {
    previousMandateAmountRef.current = previousMandateAmount;
  }, [previousMandateAmount]);

  // console.log('selectedAutopayTabValue', selectedAutopayTabValue);
  // console.log('selectedEnachTabValue', selectedEnachTabValue);
  // console.log('previousMandateAmount', previousMandateAmount);

  useEffect(() => {
    if (data) {
      form.resetFields();
      const loanAmount = (data.loanAmount as number) / 100;
      const newData: MandateLinkFormType = {
        ...data,
        loanAmount: loanAmount == 0 ? '' : loanAmount,
      };
      form.setFieldsValue(newData);
      setFormData(newData);
    }
  }, [data]);

  // console.log('mandateAmount', mandateAmount);

  useEffect(() => {
    if (CIFlist && CIFlist.length > 0) {
      form.setFieldsValue({ cIFId: CIFlist[0] });
      if (CIFlist[0]) {
        onCIFChange(CIFlist[0]);
      }
      setIsManuallyEdited(false);
    }
  }, [CIFlist]);

  const handleCIFChange = (cifId: string) => {
    onCIFChange(cifId); // Call the provided onCIFChange function
  };

  // console.log(CIFlist);
  useEffect(() => {
    if (autoPay) {
      form.validateFields();
    }
  }, [autoPay]);

  const handleSubmit = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (!selectedEnachTabValue && selectedAutopayTabValue) {
      setIsManuallyEdited(false);
      setIsReadOnly(true);
      if (mandateAmount !== 15000) {
        setPreviousMandateAmount(mandateAmount);
        previousMandateAmountRef.current = mandateAmount;
        setMandateAmount(15000);
        form.setFieldsValue({ MandateAmount: 15000 });
      }
    } else if (selectedEnachTabValue && !selectedAutopayTabValue) {
      setIsReadOnly(false);

      if (!isManuallyEdited) {
        setMandateAmount(previousMandateAmountRef.current);
        form.setFieldsValue({ MandateAmount: previousMandateAmountRef.current });
      }
    } else if (selectedEnachTabValue && selectedAutopayTabValue) {
      setIsReadOnly(false);

      if (!isManuallyEdited) {
        setMandateAmount(previousMandateAmountRef.current);
        form.setFieldsValue({ MandateAmount: previousMandateAmountRef.current });
      }
    } else if (!selectedEnachTabValue && !selectedAutopayTabValue) {
      setIsReadOnly(false);
      // setIsManuallyEdited(true);

      if (!isManuallyEdited) {
        setMandateAmount(previousMandateAmountRef.current);
        form.setFieldsValue({ MandateAmount: previousMandateAmountRef.current });
      }
    }
  }, [selectedAutopayTabValue, selectedEnachTabValue, mandateAmount, form, isManuallyEdited]);
  // console.log('selectedEnachTabValue', selectedEnachTabValue);
  // console.log('selectedAutopayTabValue', selectedAutopayTabValue);

  const handleMandateAmount = (e: any) => {
    setMandateAmount(e as number);
    setIsManuallyEdited(true);
  };

  const handleMaxBillAmount = (e: any) => {
    setMaxBillAmount(e as number);
  };

  return (
    <>
      <Spin spinning={isLoading}>
        <Form<MandateLinkFormType>
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          scrollToFirstError
          className="standard-forms"
          initialValues={formData}
        >
          <div className="row">
            <div className="form-group col-xs-12 col-sm-6 col-md-6">
              <div className="input-box">
                <Form.Item label={'CIF :'} name="cIFId" className="fieldTitle">
                  <Select size="large" placeholder="Select CIF ID" onChange={handleCIFChange}>
                    {CIFlist?.map((cifId, index) => (
                      <Select.Option key={cifId + index} value={cifId}>
                        {cifId}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            </div>

            <div className="form-group col-xs-12 col-sm-6 col-md-6">
              <div className="input-box">
                <Form.Item label={'Loan Amount ( ₹ ):'} name="loanAmount" className="fieldTitle">
                  <Input
                    placeholder={'Enter Loan Amount'}
                    disabled
                    style={{ border: '1px solid #dcdcdc' }}
                  />
                </Form.Item>
              </div>
            </div>
            <div className="form-group col-xs-12 col-sm-6 col-md-6">
              <div className="input-box">
                <Form.Item label={'Tenure (In Months) :'} name="loanTenor" className="fieldTitle">
                  <Input
                    placeholder={'Enter Tenure'}
                    disabled
                    style={{ border: '1px solid #dcdcdc' }}
                  />
                </Form.Item>
              </div>
            </div>
            <div className="form-group col-xs-12 col-sm-6 col-md-6">
              <div className="input-box">
                <Form.Item
                  label={'Maximum Billing Amount:'}
                  rules={[
                    {
                      required: true,
                      message: 'Please Enter Maximum Billing Amount',
                      type: 'number',
                    },

                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (value > getFieldValue('loanAmount')) {
                          return Promise.reject(
                            new Error('Maximum Billing Amount cannot exceed Loan Amount')
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  name=""
                  className="fieldTitle"
                >
                  <InputNumber
                    onBeforeInput={handleOnlyDgit}
                    value={maxBillAmount}
                    disabled={!data?.loanAmount ? true : false}
                    placeholder={'Enter Maximum Billing Amount'}
                    style={{ border: '1px solid #dcdcdc' }}
                    onChange={(e) => {
                      handleMaxBillAmount(e);
                    }}
                  />
                </Form.Item>
              </div>
            </div>
            <div className="form-group col-xs-12 col-sm-6 col-md-6">
              <div className="input-box">
                <Form.Item
                  label={'Mandate Amount(EMI Amount) :'}
                  name="MandateAmount"
                  rules={[
                    {
                      required: true,
                      message: 'Please Enter  Amount',
                      type: 'number',
                    },

                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (value > getFieldValue('loanAmount')) {
                          setSelectedEnachTabValue(false);
                          setSelectedAutopayTabValue(false);
                          return Promise.reject(
                            new Error('Mandate Amount cannot exceed Loan Amount')
                          );
                        } else if (value > 15000 && autoPay) {
                          setSelectedAutopayTabValue(false);
                          setAutoPay(false);
                        } else if (value < 1) {
                          setSelectedEnachTabValue(false);
                          setSelectedAutopayTabValue(false);
                          return Promise.reject(new Error('Mandate Amount cannot be less than 1'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  initialValue={data ? mandateAmount : ''}
                  className="fieldTitle"
                >
                  <InputNumber
                    placeholder={'Enter Mandate Amount'}
                    type="number"
                    value={mandateAmount}
                    disabled={isReadOnly || !data?.loanAmount}
                    onBeforeInput={handleOnlyDgit}
                    onChange={(e) => handleMandateAmount(e)}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>
      </Spin>
    </>
  );
}

export default MandateLinkForm;
